import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  Paper,
  Snackbar,
  Typography,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import axios from '../utils/api.js';
import theme from '../UserDashboard/theme.js';

const accent = theme.colors.brandDark;

function computeFinalPrice(basePrice, discountPercent) {
  const base = Number(basePrice) || 0;
  const percent = Number(discountPercent) || 0;
  return Math.round(base - (base * percent) / 100);
}

function formatMoney(amount, currency = 'INR') {
  if (amount == null || Number.isNaN(Number(amount))) return '—';
  try {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: currency || 'INR',
      maximumFractionDigits: 0,
    }).format(Number(amount));
  } catch {
    return `${currency || 'INR'} ${amount}`;
  }
}

function PlanDetail({ label, value }) {
  return (
    <Box sx={styles.detailRow}>
      <Typography sx={styles.detailLabel}>{label}</Typography>
      <Typography sx={styles.detailValue}>{value ?? '—'}</Typography>
    </Box>
  );
}

function PlanCard({ plan, onBuyPlan, buying }) {
  const currency = plan.pricing?.currency || 'INR';
  const basePrice = plan.pricing?.basePrice;
  const discountPercent = plan.pricing?.discount?.percent ?? 0;
  const finalPrice =
    plan.pricing?.discount?.finalPrice ?? computeFinalPrice(basePrice, discountPercent);
  const features = Array.isArray(plan.benefits?.features) ? plan.benefits.features : [];
  const billingCycle = plan.billing?.autoPay?.billingCycle;
  const isActive = (plan.metadata?.status || '').toLowerCase() === 'active';

  return (
    <Paper elevation={0} sx={styles.planCard}>
      <Box sx={styles.planHeader}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={styles.planName}>{plan.planName}</Typography>
          {plan.planType ? (
            <Chip
              label={plan.planType}
              size="small"
              sx={{ mt: 0.75, fontWeight: 600, fontSize: 11, height: 22 }}
            />
          ) : null}
          {plan.description ? (
            <Typography sx={styles.planDesc}>{plan.description}</Typography>
          ) : null}
        </Box>
        <Box sx={styles.priceBlock}>
          {discountPercent > 0 && basePrice != null ? (
            <Typography sx={styles.basePriceStrike}>{formatMoney(basePrice, currency)}</Typography>
          ) : null}
          <Typography sx={styles.finalPrice}>{formatMoney(finalPrice, currency)}</Typography>
          {discountPercent > 0 ? (
            <Typography sx={styles.discountNote}>{discountPercent}% off</Typography>
          ) : null}
          {billingCycle ? (
            <Typography sx={styles.billingNote}>per {billingCycle}</Typography>
          ) : null}
        </Box>
      </Box>

      <Grid container spacing={1.5} sx={{ mt: 2 }}>
        <Grid item xs={6} sm={4}>
          <PlanDetail label="Sessions / month" value={plan.benefits?.sessionsPerMonth} />
        </Grid>
        <Grid item xs={6} sm={4}>
          <PlanDetail label="Hours / session" value={plan.benefits?.hoursPerSession} />
        </Grid>
        <Grid item xs={6} sm={4}>
          <PlanDetail
            label="Hours / month"
            value={plan.hoursPerMonth ?? plan.benefits?.hoursPerMonth}
          />
        </Grid>
        <Grid item xs={6} sm={4}>
          <PlanDetail label="Validity" value={plan.validityMonths ? `${plan.validityMonths} mo` : plan.restrictions?.validityDays ? `${plan.restrictions.validityDays} days` : null} />
        </Grid>
        <Grid item xs={6} sm={4}>
          <PlanDetail
            label="Unlimited access"
            value={plan.benefits?.unlimitedAccess ? 'Yes' : 'No'}
          />
        </Grid>
        <Grid item xs={6} sm={4}>
          <PlanDetail
            label="Auto-pay"
            value={plan.billing?.autoPay?.enabled ? `Yes (${billingCycle || 'monthly'})` : 'No'}
          />
        </Grid>
      </Grid>

      {features.length > 0 ? (
        <Box sx={{ mt: 2 }}>
          <Typography sx={styles.featuresTitle}>Included</Typography>
          <Box sx={styles.featureList}>
            {features.map((f) => (
              <Box key={f} sx={styles.featureItem}>
                <CheckCircleOutlineIcon sx={{ fontSize: 16, color: theme.colors.accentSuccess }} />
                <Typography sx={styles.featureText}>{f}</Typography>
              </Box>
            ))}
          </Box>
        </Box>
      ) : null}

      <Box sx={styles.buyRow}>
        <Button
          variant="contained"
          disabled={!isActive || buying}
          onClick={() => onBuyPlan(plan)}
          startIcon={buying ? <CircularProgress size={18} color="inherit" /> : <ShoppingCartOutlinedIcon />}
          sx={styles.buyBtn}
        >
          {buying ? 'Opening checkout…' : 'Buy plan'}
        </Button>
      </Box>
    </Paper>
  );
}

function getEmail() {
  return (localStorage.getItem('email') ?? '').trim();
}

function loadRazorpayScript() {
  return new Promise((resolve, reject) => {
    if (typeof window !== 'undefined' && window.Razorpay) {
      resolve(window.Razorpay);
      return;
    }
    const existing = document.querySelector('script[src="https://checkout.razorpay.com/v1/checkout.js"]');
    if (existing) {
      existing.addEventListener('load', () => resolve(window.Razorpay));
      existing.addEventListener('error', reject);
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    script.onload = () => resolve(window.Razorpay);
    script.onerror = () => reject(new Error('Failed to load Razorpay checkout'));
    document.body.appendChild(script);
  });
}

function formatDateShort(d) {
  if (!d) return '';
  const date = new Date(d);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

/**
 * Lists subscription plans from GET /getsubscription (same as admin view).
 * @param {{ compact?: boolean, robotId?: string, onPaymentSuccess?: () => void }} props
 */
export function PaymentPlanView({ compact = false, robotId, onPaymentSuccess }) {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [buyingPlanId, setBuyingPlanId] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  const fetchPlans = useCallback(async () => {
    setLoading(true);
    setMessage('');
    setError('');
    try {
      const res = await axios.get('/getsubscription');
      const rows = res.data?.data;
      const list = Array.isArray(rows) ? rows : [];
      const active = list.filter((p) => (p.metadata?.status || '').toLowerCase() === 'active');
      setPlans(active.length ? active : list);
      if (!list.length) {
        setMessage('No subscription plans are available right now.');
      } else if (!active.length && list.length) {
        setMessage('Showing all plans — none are marked active yet.');
      }
    } catch (err) {
      const msg = err.response?.data?.message || err.response?.data?.error;
      if (err.response?.status === 404) {
        setPlans([]);
        setMessage(msg || 'No subscription plans found.');
      } else {
        setError(msg || 'Failed to load subscription plans.');
        setPlans([]);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  const mainPlans = plans.filter((p) => (p.planCategory || 'main') === 'main');
  const addonPlans = plans.filter((p) => p.planCategory === 'addon');
  const displayMain = mainPlans.length ? mainPlans : plans;

  const startPurchase = async (plan) => {
    if (!plan || buyingPlanId) return;
    const email = getEmail();
    if (!email) {
      setSnackbar({ open: true, message: 'Please sign in again to purchase a plan.', severity: 'error' });
      return;
    }

    const planId = plan._id;

    if ((plan.planCategory || 'main') !== 'main') {
      setSnackbar({
        open: true,
        message: 'Add-on plans are purchased from the Add-ons section.',
        severity: 'info',
      });
      return;
    }

    setBuyingPlanId(planId);

    try {
      const initiateRes = await axios.post('/payment/initiate-and-activate', {
        subscriptionPlanId: planId,
      });

      const payload = initiateRes.data?.data || initiateRes.data;
      const key =
        payload?.key ||
        payload?.razorpayKeyId ||
        initiateRes.data?.key ||
        initiateRes.data?.razorpayKeyId;
      const subscriptionId = payload?.subscription_id || payload?.subscriptionId;
      if (!key || !subscriptionId) {
        throw new Error('Payment could not be started. Missing Razorpay subscription details.');
      }

      const Razorpay = await loadRazorpayScript();

      await new Promise((resolve, reject) => {
        const options = {
          key,
          subscription_id: subscriptionId,
          name: 'Spotless',
          description: payload?.planName || plan.planName,
          prefill: { email },
          theme: { color: '#2563eb' },
          handler: async (response) => {
            try {
              const verifyRes = await axios.post('/payment/initiate-and-activate', {
                subscriptionPlanId: planId,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_subscription_id: response.razorpay_subscription_id,
                razorpay_signature: response.razorpay_signature,
              });
              const nextDate = verifyRes.data?.data?.nextPaymentDate;
              const baseMsg =
                verifyRes.data?.message || `Subscription started for ${plan.planName}.`;
              setSnackbar({
                open: true,
                message: nextDate
                  ? `${baseMsg} Next payment: ${formatDateShort(nextDate)}.`
                  : baseMsg,
                severity: 'success',
              });
              onPaymentSuccess?.();
              resolve();
            } catch (verifyErr) {
              reject(verifyErr);
            }
          },
          modal: {
            ondismiss: () => reject(new Error('Payment cancelled')),
          },
        };

        const rzp = new Razorpay(options);
        rzp.on('payment.failed', (resp) => {
          const msg =
            resp.error?.description || resp.error?.reason || 'Payment failed. Please try again.';
          reject(new Error(msg));
        });
        rzp.open();
      });
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        'Could not complete subscription purchase.';
      if (msg !== 'Payment cancelled') {
        setSnackbar({ open: true, message: msg, severity: 'error' });
      }
    } finally {
      setBuyingPlanId(null);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: compact ? 200 : 320 }}>
        <CircularProgress sx={{ color: theme.colors.brandPrimary }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ borderRadius: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!plans.length) {
    return (
      <Typography sx={{ fontSize: 14, color: theme.colors.textSecondary }}>
        {message || 'No subscription plans available.'}
      </Typography>
    );
  }

  return (
    <>
    <Box sx={compact ? undefined : styles.pageWrap}>
      {!compact && (
        <Typography sx={styles.pageTitle}>Subscription plans</Typography>
      )}
      {!compact && (
        <Typography sx={styles.pageSubtitle}>
          Choose a plan that fits your robot usage. Prices include any listed discount.
        </Typography>
      )}
      {message && plans.length ? (
        <Typography sx={{ fontSize: 13, color: theme.colors.textSecondary, mb: 2 }}>
          {message}
        </Typography>
      ) : null}

      <Typography sx={styles.sectionLabel}>Main plans</Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {displayMain.map((plan) => (
          <PlanCard
            key={plan._id}
            plan={plan}
            onBuyPlan={startPurchase}
            buying={buyingPlanId === plan._id}
          />
        ))}
      </Box>

      {addonPlans.length > 0 ? (
        <>
          <Typography sx={{ ...styles.sectionLabel, mt: 3 }}>Add-ons</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {addonPlans.map((plan) => (
              <PlanCard
                key={plan._id}
                plan={plan}
                onBuyPlan={startPurchase}
                buying={buyingPlanId === plan._id}
              />
            ))}
          </Box>
        </>
      ) : null}
    </Box>

    <Snackbar
      open={snackbar.open}
      autoHideDuration={5000}
      onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    >
      <Alert
        severity={snackbar.severity}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        sx={{ borderRadius: 2, width: '100%' }}
      >
        {snackbar.message}
      </Alert>
    </Snackbar>
    </>
  );
}

export default PaymentPlanView;

const styles = {
  pageWrap: {
    maxWidth: 900,
    mx: 'auto',
    py: { xs: 2, md: 3 },
    px: { xs: 2, md: 0 },
  },
  pageTitle: {
    fontSize: { xs: '1.25rem', md: '1.5rem' },
    fontWeight: 700,
    color: theme.colors.textPrimary,
    mb: 0.5,
  },
  pageSubtitle: {
    fontSize: 14,
    color: theme.colors.textSecondary,
    mb: 3,
  },
  sectionLabel: {
    fontSize: 12,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    color: theme.colors.textSecondary,
    mb: 1.5,
  },
  planCard: {
    p: { xs: 2, md: 2.5 },
    borderRadius: `${theme.radius.lg}px`,
    border: `1px solid ${theme.colors.borderDefault}`,
    bgcolor: theme.colors.bgPrimary,
    boxShadow: '0 1px 3px rgba(15, 23, 42, 0.06)',
  },
  planHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 2,
    flexWrap: 'wrap',
  },
  planName: {
    fontSize: { xs: '1.1rem', md: '1.25rem' },
    fontWeight: 700,
    color: theme.colors.textPrimary,
  },
  planDesc: {
    fontSize: 14,
    color: theme.colors.textSecondary,
    mt: 0.75,
    lineHeight: 1.5,
  },
  priceBlock: {
    textAlign: 'right',
    flexShrink: 0,
  },
  basePriceStrike: {
    fontSize: 14,
    color: theme.colors.textSecondary,
    textDecoration: 'line-through',
  },
  finalPrice: {
    fontSize: { xs: '1.35rem', md: '1.5rem' },
    fontWeight: 800,
    color: accent,
    lineHeight: 1.2,
  },
  discountNote: {
    fontSize: 12,
    fontWeight: 600,
    color: theme.colors.accentSuccess,
    mt: 0.25,
  },
  billingNote: {
    fontSize: 12,
    color: theme.colors.textSecondary,
    mt: 0.25,
  },
  detailRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: 0.25,
  },
  detailLabel: {
    fontSize: 11,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    color: theme.colors.textSecondary,
  },
  detailValue: {
    fontSize: 14,
    fontWeight: 600,
    color: theme.colors.textPrimary,
  },
  featuresTitle: {
    fontSize: 12,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    color: theme.colors.textSecondary,
    mb: 1,
  },
  featureList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 0.75,
  },
  featureItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 1,
  },
  featureText: {
    fontSize: 14,
    color: theme.colors.textPrimary,
    lineHeight: 1.4,
  },
  buyRow: {
    mt: 2.5,
    pt: 2,
    borderTop: `1px solid ${theme.colors.borderDefault}`,
    display: 'flex',
    justifyContent: 'flex-end',
  },
  buyBtn: {
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '0.9375rem',
    borderRadius: 2,
    px: 3,
    py: 1.1,
    bgcolor: accent,
    boxShadow: '0 2px 8px rgba(37, 99, 235, 0.3)',
    '&:hover': {
      bgcolor: theme.colors.brandPrimary,
      boxShadow: '0 4px 12px rgba(37, 99, 235, 0.4)',
    },
    '&.Mui-disabled': {
      bgcolor: 'rgba(37, 99, 235, 0.35)',
      color: 'rgba(255,255,255,0.9)',
    },
  },
};
